import React, { useEffect, useRef, useCallback } from 'react';
import { StyleTemplate } from '../types/template';
import TemplateCard from './TemplateCard';
import { useImagePreloader } from '../hooks/useImagePreloader';
import './TemplateGrid.css';

interface TemplateGridProps {
  templates: StyleTemplate[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onTemplateClick?: (template: StyleTemplate) => void;
}

const TemplateGrid: React.FC<TemplateGridProps> = ({
  templates,
  loading,
  hasMore,
  onLoadMore,
  onTemplateClick,
}) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { preloadTemplateImages } = useImagePreloader();

  // 预加载图片
  useEffect(() => {
    if (templates.length > 0) {
      preloadTemplateImages(templates).catch(console.error);
    }
  }, [templates, preloadTemplateImages]);

  // 使用 Intersection Observer 实现无限滚动
  const setupIntersectionObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      {
        rootMargin: '100px', // 提前100px开始加载
      }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
  }, [hasMore, loading, onLoadMore]);

  useEffect(() => {
    setupIntersectionObserver();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setupIntersectionObserver]);

  return (
    <div className="template-grid-container">
      <div className="template-grid" ref={gridRef}>
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onClick={onTemplateClick}
          />
        ))}
      </div>
      
      <div ref={loadMoreRef} className="load-more-trigger">
        {loading && (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <span>加载中...</span>
          </div>
        )}
        {!hasMore && templates.length > 10 && (
          <div className="no-more-text">没有更多</div>
        )}
      </div>
    </div>
  );
};

export default TemplateGrid;
